/**
 * ETL pipelines — master list + create dialog (ADR-0038, P4).
 *
 * The integration plane's bulk/streaming data-movement surface (sibling of
 * Workflows). Rows open the designer at `/etl/:id`. A new pipeline is seeded
 * with a minimal valid graph (`static_source → log_sink`) so the backend's
 * structural validator accepts it and the user starts from a working canvas.
 *
 * The list is GROUPED BY LANE (corsie) — pipelines without a lane come first.
 * Scheduling + lane assignment live INSIDE the editor (not here): the list only
 * SHOWS the schedule, in natural language.
 */
import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Clock, Loader2, Plus, Split, Workflow as PipeIcon, Trash2 } from "lucide-react";

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { EtlNavTabs } from "@/components/etl/EtlNavTabs";
import { etlApi, type EtlPipelineDefinitionDoc, type PipelineSummary } from "@/lib/api-etl";
import { describeCron } from "@/lib/cronText";
import { usePermissions } from "@/lib/permissions";

function starterDefinition(name: string): EtlPipelineDefinitionDoc {
  return {
    name,
    nodes: [
      { id: "source", type: "static_source", label: "Sorgente", config: { rows: [] }, position: { x: 80, y: 160 } },
      { id: "sink", type: "log_sink", label: "Log", config: {}, position: { x: 440, y: 160 } },
    ],
    edges: [{ from: "source", to: "sink", sourceHandle: "default", targetHandle: "input" }],
  };
}

function CreateDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");

  const createMut = useMutation({
    mutationFn: () =>
      etlApi.create({ name: name.trim(), description: description.trim() || undefined, definition: starterDefinition(name.trim()) }),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["etl", "list"] });
      navigate(`/etl/${p.id}`);
    },
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nuova pipeline ETL</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Nome</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="es. Import ordini notturno" autoFocus />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Descrizione</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Facoltativa" />
          </div>
          {createMut.isError && <p className="text-xs text-danger">Creazione non riuscita</p>}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annulla
          </Button>
          <Button onClick={() => createMut.mutate()} disabled={!name.trim() || createMut.isPending}>
            {createMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Crea
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface LaneGroup {
  key: string;
  laneName: string | null;
  rows: PipelineSummary[];
}

/** Group pipelines by lane; the no-lane group sorts FIRST, then lanes A→Z. */
function groupByLane(rows: PipelineSummary[]): LaneGroup[] {
  const byLane = new Map<string, LaneGroup>();
  for (const p of rows) {
    const key = p.lane_id ?? "__none__";
    if (!byLane.has(key)) byLane.set(key, { key, laneName: p.lane_name, rows: [] });
    byLane.get(key)!.rows.push(p);
  }
  return [...byLane.values()].sort((a, b) => {
    if (a.laneName === null) return -1;
    if (b.laneName === null) return 1;
    return a.laneName.localeCompare(b.laneName);
  });
}

function PipelineTable({
  rows,
  canWrite,
  canDelete,
  onOpen,
  onDelete,
}: {
  rows: PipelineSummary[];
  canWrite: boolean;
  canDelete: boolean;
  onOpen: (id: string) => void;
  onDelete: (p: PipelineSummary) => void;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-border/60">
      <Table>
        <Thead>
          <Tr>
            <Th>Nome</Th>
            <Th>Descrizione</Th>
            <Th className="w-56">Pianificazione</Th>
            <Th className="w-28">Stato</Th>
            {canDelete && <Th className="w-16 text-right">Azioni</Th>}
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((p) => {
            const schedule = describeCron(p.schedule_cron, p.schedule_timezone);
            return (
              <Tr key={p.id} className="cursor-pointer" onClick={() => onOpen(p.id)}>
                <Td className="font-medium text-foreground">{p.name}</Td>
                <Td className="max-w-md truncate text-xs text-muted-foreground">
                  {p.description || "—"}
                </Td>
                <Td className="text-xs text-muted-foreground">
                  {schedule ? (
                    <span className="inline-flex items-center gap-1 text-accent">
                      <Clock className="h-3 w-3" /> {schedule}
                    </span>
                  ) : (
                    "—"
                  )}
                </Td>
                <Td className="text-xs">
                  <span className={p.enabled ? "text-emerald-500" : "text-muted-foreground"}>
                    {p.enabled ? "Attiva" : "Disattivata"}
                  </span>
                  {!p.active_version_id && <span className="ml-1 text-warning">· bozza</span>}
                </Td>
                {canDelete && (
                  <Td className="text-right" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => onDelete(p)}
                      className="rounded p-1 text-muted-foreground hover:bg-elevated hover:text-danger"
                      aria-label="Elimina"
                      disabled={!canWrite && !canDelete}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </Td>
                )}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </div>
  );
}

export function EtlPipelinesPage() {
  const { can } = usePermissions();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [creating, setCreating] = React.useState(false);

  const q = useQuery({ queryKey: ["etl", "list"], queryFn: () => etlApi.list() });
  const delMut = useMutation({
    mutationFn: (id: string) => etlApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["etl", "list"] }),
  });

  const rows = q.data ?? [];
  const groups = React.useMemo(() => groupByLane(rows), [rows]);
  const canDelete = can("etl", "delete");
  const canWrite = can("etl", "write");

  const onDelete = (p: PipelineSummary) => {
    if (window.confirm(`Eliminare la pipeline "${p.name}"?`)) delMut.mutate(p.id);
  };

  // Only show lane section headers when there's actually more than one group
  // (or a single laned group) — a flat no-lane list stays clean.
  const showHeaders = groups.length > 1 || (groups.length === 1 && groups[0].laneName !== null);

  return (
    <PageContainer>
      <PageHeader
        title="Pipeline ETL"
        description="Movimento dati bulk e streaming — sorgenti, trasformazioni, destinazioni."
        actions={
          can("etl", "write") && (
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> Nuova pipeline
            </Button>
          )
        }
      />

      <PageContent>
        <EtlNavTabs active="pipelines" />
        {q.isLoading ? (
          <div className="flex items-center gap-2 p-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Caricamento…
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={PipeIcon}
            title="Nessuna pipeline"
            description="Crea la prima pipeline ETL per spostare e trasformare i dati."
          />
        ) : (
          <div className="space-y-6">
            {groups.map((g) => (
              <div key={g.key} className="space-y-2">
                {showHeaders && (
                  <h2 className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    {g.laneName ? (
                      <>
                        <Split className="h-3.5 w-3.5" /> Corsia · {g.laneName}
                      </>
                    ) : (
                      "Senza corsia"
                    )}
                    <span className="text-muted-foreground/60">({g.rows.length})</span>
                  </h2>
                )}
                <PipelineTable
                  rows={g.rows}
                  canWrite={canWrite}
                  canDelete={canDelete}
                  onOpen={(id) => navigate(`/etl/${id}`)}
                  onDelete={onDelete}
                />
              </div>
            ))}
          </div>
        )}
      </PageContent>

      {creating && <CreateDialog onClose={() => setCreating(false)} />}
    </PageContainer>
  );
}
